
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@/hooks/use-user";
import { ArrowLeft, Edit, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

type CollectionItem = {
  id: string;
  post: {
    id: string;
    image_url: string;
    title?: string;
    description?: string;
  };
};

type Collection = {
  id: string;
  title: string;
  created_at: string;
  cover_image_url?: string;
};

const CollectionDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isLoading: isUserLoading } = useUser();
  const { toast } = useToast();
  
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [coverUrl, setCoverUrl] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Fetch collection details
  const { data: collection, isLoading: isCollectionLoading, refetch: refetchCollection } = useQuery({
    queryKey: ["collection", id],
    queryFn: async () => {
      if (!id || !user?.id) return null;
      
      const { data, error } = await supabase
        .from("collections")
        .select("*")
        .eq("id", id)
        .eq("user_id", user.id)
        .single();
      
      if (error) throw error;
      
      setNewTitle(data.title);
      setCoverUrl(data.cover_image_url || "");
      return data as Collection;
    },
    enabled: !!id && !!user?.id,
  });

  // Fetch items in the collection
  const { data: collectionItems, isLoading: isItemsLoading, refetch: refetchItems } = useQuery({
    queryKey: ["collectionItems", id],
    queryFn: async () => {
      if (!id) return [];
      
      const { data, error } = await supabase
        .from("collection_items")
        .select(`
          id,
          post:posts (
            id,
            image_url,
            title,
            description
          )
        `)
        .eq("collection_id", id);
      
      if (error) throw error;
      return data as CollectionItem[];
    },
    enabled: !!id,
  });

  // Update collection title and cover
  const handleUpdateCollection = async () => {
    if (!id || !user?.id || !newTitle.trim()) {
      toast({
        title: "Erro",
        description: "Título não pode ser vazio",
        variant: "destructive"
      });
      return;
    }

    const updates = {
      title: newTitle.trim(),
      cover_image_url: coverUrl || null
    };

    const { error } = await supabase
      .from("collections")
      .update(updates)
      .eq("id", id)
      .eq("user_id", user.id);

    if (error) {
      toast({
        title: "Erro",
        description: "Falha ao atualizar coleção",
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "Sucesso",
      description: "Coleção atualizada"
    });
    
    setIsEditDialogOpen(false);
    refetchCollection();
  };

  // Delete collection
  const handleDeleteCollection = async () => {
    if (!id || !user?.id) return;

    const { error } = await supabase
      .from("collections")
      .delete()
      .eq("id", id)
      .eq("user_id", user.id);

    if (error) {
      toast({
        title: "Erro",
        description: "Falha ao excluir coleção",
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "Sucesso",
      description: "Coleção excluída"
    });
    navigate("/collections");
  };

  // Remove item from collection
  const handleRemoveItem = async (itemId: string) => {
    const { error } = await supabase
      .from("collection_items")
      .delete()
      .eq("id", itemId);

    if (error) {
      toast({
        title: "Erro",
        description: "Falha ao remover item",
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "Sucesso",
      description: "Item removido da coleção"
    });
    refetchItems();
  };

  // Loading state
  if (isUserLoading || isCollectionLoading) {
    return <div className="container py-8 flex items-center justify-center min-h-[60vh]">Carregando...</div>;
  }

  // Not logged in or collection not found
  if (!user || !collection) {
    return (
      <div className="container py-8 flex flex-col items-center justify-center min-h-[60vh]">
        <p className="text-lg mb-4">Coleção não encontrada ou você não tem permissão para visualizá-la.</p>
        <Button onClick={() => navigate("/collections")}>Voltar para Coleções</Button>
      </div>
    );
  }

  // Get first image as cover if no cover image is set but items exist
  const coverImage = collection.cover_image_url || (collectionItems?.length > 0 && collectionItems[0]?.post?.image_url);

  return (
    <div className="container py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate("/collections")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold">{collection.title}</h1>
        </div>
        
        <div className="flex gap-2">
          {/* Edit Dialog */}
          <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Edit className="h-4 w-4 mr-2" />
                Editar
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Editar Coleção</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label htmlFor="title" className="text-sm font-medium">Nome da Coleção</label>
                  <Input
                    id="title"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    placeholder="Nome da coleção"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="coverUrl" className="text-sm font-medium">URL da capa (opcional)</label>
                  <Input
                    id="coverUrl"
                    value={coverUrl}
                    onChange={(e) => setCoverUrl(e.target.value)}
                    placeholder="URL da imagem de capa"
                  />
                  {coverUrl && (
                    <div className="mt-2 border rounded-md overflow-hidden">
                      <img
                        src={coverUrl}
                        alt="Capa da coleção"
                        className="w-full h-40 object-cover"
                        onError={() => setCoverUrl("")}
                      />
                    </div>
                  )}
                </div>
                <Button onClick={handleUpdateCollection} className="w-full">
                  Salvar alterações
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Delete Dialog */}
          <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm">
                <Trash2 className="h-4 w-4 mr-2" />
                Excluir
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Tem certeza?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta ação não pode ser desfeita. Isso excluirá permanentemente a coleção
                  "{collection.title}" e removerá todos os itens salvos nela.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteCollection}>
                  Sim, excluir coleção
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {isItemsLoading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="h-64 animate-pulse bg-muted"></Card>
          ))}
        </div>
      ) : collectionItems?.length ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
          {collectionItems.map((item) => (
            <Card key={item.id} className="group relative overflow-hidden">
              <div 
                className="aspect-square w-full cursor-pointer" 
                onClick={() => item.post?.id && navigate(`/pin/${item.post.id}`)}
              >
                {item.post?.image_url && (
                  <img 
                    src={item.post.image_url} 
                    alt={item.post?.title || "Item da coleção"}
                    className="h-full w-full object-cover" 
                  />
                )}
              </div>
              <div className="p-3">
                <h3 className="font-medium truncate">{item.post?.title || "Sem título"}</h3>
                <p className="text-sm text-muted-foreground truncate">{item.post?.description || "Sem descrição"}</p>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                className="absolute right-2 top-2 opacity-0 transition-opacity group-hover:opacity-100 bg-black/50 hover:bg-black/70 text-white" 
                onClick={() => handleRemoveItem(item.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </Card>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <p className="text-muted-foreground">Esta coleção está vazia</p>
          <Button onClick={() => navigate("/")} variant="outline">
            Explorar conteúdo para salvar
          </Button>
        </div>
      )}
    </div>
  );
};

export default CollectionDetails;
